// Fork button — shown on Library entry cards. Opens a squad picker modal
// (when the entry needs a target squad). Solo users get a "Join a squad to
// fork" overlay instead of a working button.
import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { GitFork, Loader2, Users, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/hooks/useAuth";
import { useMySquads } from "@/hooks/useSquads";
import { useEntitlements } from "@/hooks/useEntitlements";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useForkLibraryEntry } from "@/hooks/useLibraryMutations";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import type { LibraryEntry } from "@/hooks/useLibrary";
import type { LibraryKind } from "@/hooks/useLibrary";
import ForkSkillsConfirmDialog, {
  extractEntrySkillNums,
} from "@/components/library/ForkSkillsConfirmDialog";

interface Props {
  entry: LibraryEntry;
  variant?: "default" | "outline" | "ghost";
  size?: "sm" | "default";
  className?: string;
  /** When true, shows a "Join a squad to fork" pill for non-members. */
  showSquadGate?: boolean;
}

const ForkLibraryButton = ({
  entry,
  variant = "outline",
  size = "sm",
  className,
  showSquadGate = true,
}: Props) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: squads = [] } = useMySquads();
  const { isPaid, isAdmin, startCheckout } = useEntitlements();
  const { settings } = useSiteSettings();
  const fork = useForkLibraryEntry();
  const [open, setOpen] = useState(false);
  const [pickedSquad, setPickedSquad] = useState<string | null>(null);
  // Post-fork confirmation dialog state. We keep the redirect path
  // returned by the edge function around so the dialog's primary
  // action can open the fork after the user has reviewed the skill
  // delta. If the source entry tags no skills, we skip the dialog
  // entirely (see handleFork below).
  const [skillDialogOpen, setSkillDialogOpen] = useState(false);
  const [pendingRedirect, setPendingRedirect] = useState<string | null>(null);
  // Capture the freshly-forked resource so the post-fork dialog can
  // offer a "publish my fork (private/public)" toggle without an extra
  // round-trip — the kind+id come straight from the edge function.
  const [forkedKind, setForkedKind] = useState<string | null>(null);
  const [forkedId, setForkedId] = useState<string | null>(null);
  const sourceSkillNums = useMemo(
    () => extractEntrySkillNums(entry.payload as Record<string, unknown> | null),
    [entry.payload],
  );

  const flagOff = settings.feature_flags?.library_fork_enabled === false;
  const requiresSquad = entry.kind === "protocol_run";
  const noSquads = (squads ?? []).length === 0;

  // Default the picker to the first squad once the list resolves.
  useMemo(() => {
    if (!pickedSquad && squads && squads.length > 0) setPickedSquad(squads[0].id);
  }, [squads, pickedSquad]);

  if (flagOff) return null;

  // Sign-in gate.
  if (!user) {
    return (
      <Button
        variant={variant}
        size={size}
        onClick={() => navigate("/account")}
        className={cn("font-mono", className)}
      >
        <Lock className="w-3.5 h-3.5 mr-1.5" /> Sign in to fork
      </Button>
    );
  }

  // Squad gate for solo users (only when fork strictly requires a squad).
  if (showSquadGate && requiresSquad && noSquads) {
    return (
      <Button
        variant={variant}
        size={size}
        onClick={() => navigate("/squads")}
        className={cn("font-mono border-dashed", className)}
        title="Forking protocol runs requires a squad"
      >
        <Users className="w-3.5 h-3.5 mr-1.5" /> Join a squad to fork
      </Button>
    );
  }

  const handleFork = async () => {
    if (requiresSquad && !pickedSquad) {
      toast.error("Pick a squad to fork into");
      return;
    }
    try {
      const result = await fork.mutateAsync({
        entry_id: entry.id,
        target_squad_id: pickedSquad,
      });
      setOpen(false);
      setForkedKind(result.resource_kind);
      setForkedId(result.resource_id);
      // Surface a recovery toast when the server forked the resource
      // but could not credit the tagged mastery skills (currently
      // always the case for non-chat resource kinds — see
      // library-fork/index.ts). The user can jump straight to /mastery
      // to record the skills themselves so their progress isn't lost.
      if (
        result.skills_recorded === false &&
        (result.skills_tagged ?? 0) > 0
      ) {
        const n = result.skills_tagged ?? 0;
        toast.warning(
          `Forked, but ${n} mastery skill${n === 1 ? "" : "s"} weren't credited`,
          {
            description:
              "Open your Mastery page to record them manually so this fork still moves you forward.",
            duration: 9000,
            action: {
              label: "Open Mastery",
              onClick: () => navigate("/roadmap"),
            },
          },
        );
      }
      // When the source has tagged mastery skills, show the
      // confirmation dialog FIRST so the user understands the impact
      // before we whisk them away. Otherwise fall back to the prior
      // toast-and-redirect behaviour.
      if (sourceSkillNums.length > 0) {
        toast.success("Forked! Reviewing your skill impact…");
        setPendingRedirect(result.redirect);
        setSkillDialogOpen(true);
      } else {
        // Even when no skills are tagged, give the user the
        // private/public publish toggle on their fork. Open the
        // dialog without skill sections — it now also serves as the
        // post-fork "what next?" surface.
        toast.success("Forked! Opening your copy…");
        setPendingRedirect(result.redirect);
        setSkillDialogOpen(true);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Fork failed";
      // Pro upgrade hint
      if (msg.toLowerCase().includes("operator") || msg.toLowerCase().includes("pro")) {
        toast.error(msg, {
          action: { label: "Upgrade", onClick: () => startCheckout() },
        });
      }
    }
  };

  // Pro hint up-front (server still enforces).
  const proLocked = !isPaid && !isAdmin;

  // For prompts/guides/research the squad picker is optional; we can fork
  // straight away (the resource is owned by the user anyway).
  const skipPicker = !requiresSquad && (noSquads || !showSquadGate);

  if (skipPicker) {
    return (
      <>
        <Button
          variant={variant}
          size={size}
          onClick={handleFork}
          disabled={fork.isPending}
          className={cn("font-mono", className)}
        >
          {fork.isPending ? (
            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
          ) : (
            <GitFork className="w-3.5 h-3.5 mr-1.5" />
          )}
          Fork
        </Button>
        <ForkSkillsConfirmDialog
          open={skillDialogOpen}
          onOpenChange={setSkillDialogOpen}
          sourceSkillNums={sourceSkillNums}
          entryTitle={entry.title}
          redirectPath={pendingRedirect ?? undefined}
          forkedResourceKind={(forkedKind ?? undefined) as LibraryKind | undefined}
          forkedResourceId={forkedId ?? undefined}
          sourceTags={entry.tags ?? []}
        />
      </>
    );
  }

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={() => setOpen(true)}
        className={cn("font-mono", className)}
      >
        <GitFork className="w-3.5 h-3.5 mr-1.5" /> Fork
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-mono">Fork into a squad</DialogTitle>
            <DialogDescription className="font-mono text-xs">
              Pick the squad that should own the new copy. Completion state is
              preserved so you pick up from the next unfinished step.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {squads.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setPickedSquad(s.id)}
                className={cn(
                  "w-full text-left rounded-md border p-2.5 transition-colors",
                  pickedSquad === s.id
                    ? "border-primary bg-primary/10"
                    : "border-border hover:border-primary/40",
                )}
              >
                <div className="font-mono text-sm font-semibold">{s.name}</div>
                {s.description && (
                  <div className="text-[10px] font-mono text-muted-foreground line-clamp-1">
                    {s.description}
                  </div>
                )}
              </button>
            ))}
            {squads.length === 0 && (
              <p className="text-xs font-mono text-muted-foreground py-2">
                No squads yet — <button onClick={() => navigate("/squads")} className="text-primary underline">create one</button>.
              </p>
            )}
          </div>
          {proLocked && (
            <p className="text-[10px] font-mono text-warning">
              Forking requires Operator — we'll prompt you to upgrade if needed.
            </p>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="font-mono">
              Cancel
            </Button>
            <Button
              onClick={handleFork}
              disabled={fork.isPending || !pickedSquad}
              className="font-mono"
            >
              {fork.isPending ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              ) : (
                <GitFork className="w-3.5 h-3.5 mr-1.5" />
              )}
              Fork it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ForkSkillsConfirmDialog
        open={skillDialogOpen}
        onOpenChange={setSkillDialogOpen}
        sourceSkillNums={sourceSkillNums}
        entryTitle={entry.title}
        redirectPath={pendingRedirect ?? undefined}
        forkedResourceKind={(forkedKind ?? undefined) as LibraryKind | undefined}
        forkedResourceId={forkedId ?? undefined}
        sourceTags={entry.tags ?? []}
      />
    </>
  );
};

export default ForkLibraryButton;
